import { useEffect, useRef } from 'react';
import useStore from '../../../store';
import { drawBoard } from '../renderers/boardRenderer';

function useCanvasRenderer() {
  // Триггеры для перерисовки
  const puzzleRedraw = useStore((state) => state.puzzleRedraw); // Для полной перерисовки кешей
  const puzzleRender = useStore((state) => state.puzzleRender); // Для копирования кешей / обновления динамики

  const debounceTimeoutRef = useRef(null);
  const animationFrameRef = useRef(null); // Для requestAnimationFrame

  const store = useStore.getState(); // Доступ к ref один раз при инициализации хука
  const { ref } = store;

  // Функция рендеринга
  function redrawAll(options = {}) {
    const currentStore = useStore.getState(); // Получаем свежее состояние для рендера
    const currentRef = currentStore.ref; // и ref

    // Получаем данные для канвасов
    const boardCtx = currentRef.puzzle.boardCanvas?.context;
    const boardDims = currentRef.puzzle.boardCanvas?.dimensions;
    const trayCtx = currentRef.puzzle.trayCanvas?.context;
    const trayDims = currentRef.puzzle.trayCanvas?.dimensions;

    const state = currentRef.puzzle.state;
    const renderOptions = currentStore.redrawOptions || options; // Используем опции из store или переданные

     // Отменяем предыдущий кадр анимации, если он был запланирован
     if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
     }

    // Планируем отрисовку на следующий кадр анимации
    animationFrameRef.current = requestAnimationFrame(() => {

        // Проверяем, инициализирован ли пазл
        if (!state.isInitialized && !state.isLoading) {
            // Отрисовка состояния ожидания или ошибки на канвасе доски
            if (boardCtx && boardDims) {
                boardCtx.clearRect(0, 0, boardDims.width, boardDims.height);
                boardCtx.fillStyle = 'grey';
                boardCtx.font = '16px sans-serif';
                boardCtx.textAlign = 'center';
                boardCtx.fillText(state.gameError?.error || 'Initializing puzzle...', boardDims.width / 2, boardDims.height / 2);
            }
            // Очищаем трей
            if (trayCtx && trayDims) {
                trayCtx.clearRect(0, 0, trayDims.width, trayDims.height);
            }
            return;
        }

        // Индикатор загрузки
        if (state.isLoading) {
             if (boardCtx && boardDims) {
                boardCtx.clearRect(0, 0, boardDims.width, boardDims.height);
                boardCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
                boardCtx.font = '16px sans-serif';
                boardCtx.textAlign = 'center';
                boardCtx.fillText('Loading...', boardDims.width / 2, boardDims.height / 2);
             }
             if (trayCtx && trayDims) {
                trayCtx.clearRect(0, 0, trayDims.width, trayDims.height);
             }
             return;
        }


        // --- Отрисовка доски ---
        if (boardCtx && boardDims?.width > 0 && boardDims?.height > 0) {
            // Не очищаем здесь, очистка происходит внутри drawBoard (в начале или перед рисованием плейсхолдера)
            // boardCtx.clearRect(0, 0, boardDims.width, boardDims.height);
            drawBoard(boardCtx, null /* boardArea не используется */, currentStore, renderOptions);
        }

        animationFrameRef.current = null; // Сбрасываем ref после выполнения
    });
  }



  // Эффект для puzzleRedraw (полная перерисовка с debounce)
  useEffect(() => {
    // Используем debounce для затратных перерисовок
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    const currentRedrawOptions = useStore.getState().redrawOptions;
    const threshold = currentRedrawOptions?.threshold ?? 0; // Порог debounce по умолчанию

    if (threshold) {
      debounceTimeoutRef.current = setTimeout(() => {
        redrawAll(currentRedrawOptions); // Вызываем render с опциями из store
        debounceTimeoutRef.current = null;
      }, threshold);
    } else {
      redrawAll(currentRedrawOptions); 
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
       if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
       }
    };
  }, [puzzleRedraw]); // Реагируем на puzzleRedraw
}

export default useCanvasRenderer;